/* HANNE BLOCH product page, the Styling tab (hb-website-building 6.4.15; built 21 Oct 2026). Behaviour of
 * snippets/hb-pdp-styling. The strip is the one thing that scrolls: a swipe moves along the looks, as the Instagram
 * rows under the tabs (the browser does it; the head's arrows move it by a card on a desktop, and show only when
 * there is more than fits). Inside a card the picture turns to the film (or a piece's packshot) with the small
 * arrows and dots, or a tap on a look's picture: a slide by whole widths (a CSS variable on the rail), never a
 * scroll, so a swipe over a picture moves the strip and nothing else (Emilie, 21 Oct: "the photos just move a bit,
 * they should not do that"). "Watch the styling video" brings the first film into view and turns to it ("See the
 * packshot", which turned every piece at once, went on 26 Oct: Emilie, "this is not needed"). The films are written with data-hb-sty-src and handed to the
 * house loader (assets/hb-video.js, which plays a film only while it is on screen and never loads one that is not)
 * the first time the tab is opened, so a closed tab costs nothing. No dependency on the loader: without it the film
 * plays from its own source once the tab is open. One instance per product page; the quick view's copy of the
 * details sheet (sections/hb-qv-fit) is wired the same way when it appears. */
(function () {
  'use strict';
  var VERSION = '2026-10-26-styling4'; // styling3 without "See the packshot"

  function settle(el, left) {
    // a smooth scroll that never ran (a hidden tab, a page in the background): land there anyway
    setTimeout(function () { if (Math.abs(el.scrollLeft - left) > 2) el.scrollLeft = left; }, 450);
  }

  // a card: its picture turns between its slides (picture, second picture, film; or a piece's model and packshot)
  function wireCard(card) {
    if (card.__hbSty) return;
    card.__hbSty = true;
    var rail = card.querySelector('[data-hb-sty-rail]');
    if (!rail) return;
    var dots = card.querySelectorAll('.hb-sty__dots i');
    var n = rail.children.length;
    var at = 0;
    function go(k) {
      if (n < 2) return;
      k = ((k % n) + n) % n;
      at = k;
      rail.style.setProperty('--hb-sty-at', String(k));
      for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('is-on', i === at);
    }
    var prev = card.querySelector('[data-hb-sty-prev]');
    var next = card.querySelector('[data-hb-sty-next]');
    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(at - 1); });
    if (next) next.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); go(at + 1); });
    // a tap on a look's picture turns it too (a piece's card is a link to its page, so not there)
    var pic = card.querySelector('.hb-sty__pic');
    if (pic && n > 1 && card.tagName !== 'A') {
      pic.classList.add('is-turnable');
      pic.addEventListener('click', function (e) {
        if (e.target.closest('[data-hb-sty-prev],[data-hb-sty-next]')) return;
        go(at + 1);
      });
    }
    card.__hbStyGo = go;
    card.__hbStyAt = function () { return at; };
    card.__hbStyN = n;
  }

  // a strip and the arrows in the head above it: the arrows move it by a card; they show only when there is more
  function wireStrip(head, strip) {
    if (!head || !strip || strip.__hbSty) return;
    strip.__hbSty = true;
    var nav = head.querySelector('[data-hb-sty-nav]');
    if (!nav) return;
    var prev = nav.querySelector('[data-hb-sty-navprev]');
    var next = nav.querySelector('[data-hb-sty-navnext]');
    function step() {
      var c = strip.querySelector('[data-hb-sty-card]');
      var gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
      return c ? c.getBoundingClientRect().width + gap : strip.clientWidth / 2;
    }
    function paint() {
      var more = strip.scrollWidth > strip.clientWidth + 4;
      nav.classList.toggle('is-more', more);
      if (prev) prev.disabled = strip.scrollLeft <= 2;
      if (next) next.disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
    }
    // by a card from the nearest card edge; a quick second click counts from where the first one is going
    var want = null, wantAt = 0;
    function by(d) {
      var now = Date.now();
      var s = step() || 1;
      var base = (want !== null && now - wantAt < 700) ? want : strip.scrollLeft;
      var left = Math.max(0, Math.min((Math.round(base / s) + d) * s, strip.scrollWidth - strip.clientWidth));
      want = left; wantAt = now;
      strip.scrollTo({ left: left, behavior: 'smooth' });
      settle(strip, left);
      setTimeout(paint, 500);
    }
    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); by(-1); });
    if (next) next.addEventListener('click', function (e) { e.preventDefault(); by(1); });
    var t = 0;
    strip.addEventListener('scroll', function () {
      if (t) return;
      t = setTimeout(function () { t = 0; paint(); }, 80);
    }, { passive: true });
    window.addEventListener('resize', paint);
    strip.__hbStyPaint = paint;
    paint();
  }

  // the films: from data-hb-sty-src to data-hb-src, then the loader takes over (or a plain source without it)
  function handFilms(root) {
    var vids = root.querySelectorAll('video[data-hb-sty-src]');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      var src = v.getAttribute('data-hb-sty-src');
      var hls = v.getAttribute('data-hb-sty-hls');
      v.removeAttribute('data-hb-sty-src');
      v.removeAttribute('data-hb-sty-hls');
      if (window.HBVideo && typeof window.HBVideo.scan === 'function') {
        v.setAttribute('data-hb-src', src);
        if (hls) v.setAttribute('data-hb-hls', hls);
      } else {
        var first = (src.split(',')[0] || '').trim().split(' ')[0];
        if (first) { v.src = first; v.load(); }
      }
    }
    if (vids.length && window.HBVideo && typeof window.HBVideo.scan === 'function') window.HBVideo.scan(root);
  }

  function wire(root) {
    var cards = root.querySelectorAll('[data-hb-sty-card]');
    for (var i = 0; i < cards.length; i++) wireCard(cards[i]);
    var heads = root.querySelectorAll('.hb-sty__head');
    for (var j = 0; j < heads.length; j++) {
      var strip = heads[j].nextElementSibling;
      if (strip && strip.matches('.hb-sty__strip')) wireStrip(heads[j], strip);
    }
    // "Watch the styling video": the first film into view, and the card turned to it
    var film = root.querySelector('[data-hb-sty-film]');
    if (film && !film.__hbSty) {
      film.__hbSty = true;
      film.addEventListener('click', function (e) {
        e.preventDefault();
        handFilms(root);
        var slide = root.querySelector('.hb-sty__slide--film');
        var card = slide && slide.closest('[data-hb-sty-card]');
        if (!card) return;
        var strip = card.closest('.hb-sty__strip');
        if (strip) {
          var left = card.offsetLeft - strip.offsetLeft;
          strip.scrollTo({ left: left, behavior: 'smooth' });
          settle(strip, Math.max(0, Math.min(left, strip.scrollWidth - strip.clientWidth)));
        }
        var rail = card.querySelector('[data-hb-sty-rail]');
        var k = Array.prototype.indexOf.call(rail.children, slide);
        setTimeout(function () { card.__hbStyGo(k); }, 80);
      });
    }
  }

  // the tab opens: the films are handed over then, not before (the panel may sit hidden in the column on desktop),
  // and the strips measure themselves (they had no width while hidden)
  function watchTab(root) {
    var panel = root.closest('[data-hb-panel]');
    if (!panel || panel.__hbSty) return;
    panel.__hbSty = true;
    var check = function () {
      if (!panel.classList.contains('is-hidden')) {
        handFilms(root);
        var strips = root.querySelectorAll('.hb-sty__strip');
        for (var i = 0; i < strips.length; i++) if (strips[i].__hbStyPaint) strips[i].__hbStyPaint();
      }
    };
    if ('MutationObserver' in window) {
      new MutationObserver(check).observe(panel, { attributes: true, attributeFilter: ['class'] });
    }
    check();
  }

  function init() {
    var roots = document.querySelectorAll('[data-hb-sty]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__hbStyInit) continue;
      roots[i].__hbStyInit = true;
      wire(roots[i]);
      watchTab(roots[i]);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // the quick view (or a re-rendered section) may add a copy later
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.matches('[data-hb-sty]') || n.querySelector('[data-hb-sty]'))) { init(); return; }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.HBStyling = { version: VERSION, init: init };
})();
